import { useNavigate } from 'react-router';
import { useSnapshot } from 'valtio';

import * as updatedProductStore from '@cartwhl/common/stores/updatedProductStore';
import { StripeCustomer, StripeProduct } from '@cartwhl/common/types';
import Text from '@cartwhl/ui/base/TextNew';
import Button from '@cartwhl/ui/input/Button';

import { getStripeCustomerIdForBilling } from '@cartwhl/common/utils/organizationWorkspace';

import { authStore, customerStore, userStore } from '../../../stores/global';
import type { PriceInterval } from '../types';
import type { PricingPlanAccent } from './pricingPageData';
import {
  getCurrentSubscriptionProductId,
  getPricingPlanCTA,
  handlePricingPlanSelection,
  hasExistingSubscriptionForPricing,
} from './pricingPlanActions';

interface UpdatedPlanCardProps {
  product: StripeProduct | null;
  displayName: string;
  description: string;
  creditsValue: string;
  creditsPeriodLabel: string;
  interval: PriceInterval;
  periodLabel: string;
  accent?: PricingPlanAccent;
  showInlineYearlyTotal?: boolean;
  yearlyDisplayPrice?: string;
  yearlyTotalLabel?: string;
  onCreditsClick?: () => void;
}

const cardStyles: Record<
  PricingPlanAccent,
  {
    cardClassName: string;
    buttonClassName: string;
    creditsClassName: string;
    showDot: boolean;
  }
> = {
  neutral: {
    cardClassName: 'border-primary bg-secondary',
    buttonClassName: 'bg-black text-white hover:bg-gray-90',
    creditsClassName: 'text-blue',
    showDot: false,
  },
  primary: {
    cardClassName: 'border-blue bg-blue/10',
    buttonClassName: '',
    creditsClassName: 'text-blue',
    showDot: true,
  },
  success: {
    cardClassName: 'border-green-50 bg-green-50/10',
    buttonClassName: 'bg-green-35 text-primary hover:bg-green-50',
    creditsClassName: 'text-blue',
    showDot: true,
  },
};

export default function UpdatedPlanCard({
  product,
  displayName,
  description,
  creditsValue,
  creditsPeriodLabel,
  interval,
  periodLabel,
  accent = 'neutral',
  showInlineYearlyTotal = false,
  yearlyDisplayPrice,
  yearlyTotalLabel,
  onCreditsClick,
}: UpdatedPlanCardProps) {
  const navigate = useNavigate();
  const { authStatus } = useSnapshot(authStore.state);
  const { customer } = useSnapshot(customerStore.state);
  const { organizationInfo, userInfo } = useSnapshot(userStore.state);
  const price = updatedProductStore.getPriceByProductAndMode(
    product?.id,
    interval,
  );
  const subscriberPremiumStatus = customerStore.getSubscriberPremiumStatus(
    customer as StripeCustomer,
  );
  const hasExistingSubscription = hasExistingSubscriptionForPricing(
    subscriberPremiumStatus,
    organizationInfo?.inStripeFreeTrialPeriod,
  );
  const currentProductId = getCurrentSubscriptionProductId(
    customer as StripeCustomer | undefined,
    organizationInfo,
    userInfo,
  );
  const stripeCustomerId = getStripeCustomerIdForBilling(
    userInfo,
    organizationInfo,
  );
  const styles = cardStyles[accent];

  const cta = getPricingPlanCTA(
    authStatus,
    hasExistingSubscription,
    currentProductId,
    product?.id,
    displayName,
  );

  if (!price) {
    return (
      <div
        className={`flex min-h-[320px] animate-pulse flex-col rounded-2xl border px-6 py-7 lg:min-h-[340px] ${styles.cardClassName}`}
      >
        <div className="bg-secondary mb-6 h-7 w-2/3 rounded" />
        <div className="mb-8">
          <div className="bg-secondary mb-4 h-4 w-11/12 rounded" />
          <div className="space-y-2">
            <div className="bg-secondary h-3 w-3/4 rounded" />
            <div className="bg-secondary h-3 w-4/5 rounded" />
          </div>
        </div>
        <div className="bg-secondary mb-8 mt-auto h-16 w-1/2 rounded" />
        <div className="bg-secondary h-10 w-full rounded" />
      </div>
    );
  }

  const priceAmount =
    interval === 'year' && yearlyDisplayPrice
      ? yearlyDisplayPrice
      : formatDisplayMoney(price.unit_amount, interval);
  const annualPriceAmount =
    interval === 'year' && showInlineYearlyTotal
      ? (yearlyTotalLabel ?? formatAnnualMoney(price.unit_amount))
      : null;
  const billingNote =
    interval === 'year' && !showInlineYearlyTotal ? 'billed annually' : null;

  return (
    <div
      className={`flex h-full flex-col rounded-2xl border px-6 py-7 ${styles.cardClassName}`}
    >
      <Text
        as="h2"
        token={{ base: '20/medium', lg: '36/medium' }}
        className="flex items-center gap-3 text-pretty"
      >
        {styles.showDot && (
          <span
            className={`inline-block h-5 w-5 rounded-full lg:h-6 lg:w-6 ${
              accent === 'success' ? 'bg-green-50' : 'bg-blue'
            }`}
          />
        )}
        {displayName}
      </Text>

      <div className="grow pt-6">
        <Text as="p" token="16" className="text-secondary">
          {description}
        </Text>
      </div>

      <div className="mt-auto flex flex-col gap-6 pt-8">
        <div className="flex flex-col gap-6">
          <div className="flex items-end gap-2 whitespace-nowrap">
            <Text token={{ base: 'display/40/bold', lg: 'display/64/bold' }}>
              ${priceAmount}
            </Text>
            <Text token="20/medium">{periodLabel}</Text>
            {annualPriceAmount && (
              <Text token="16/medium" className="text-tertiary">
                (billed yearly)
              </Text>
            )}
          </div>
          <Text token="16/medium" className="text-tertiary">
            {onCreditsClick ? (
              <button
                type="button"
                onClick={onCreditsClick}
                className={`font-inherit inline cursor-pointer appearance-none bg-transparent p-0 underline ${styles.creditsClassName}`}
              >
                {creditsValue}
              </button>
            ) : (
              <span className={`underline ${styles.creditsClassName}`}>
                {creditsValue}
              </span>
            )}{' '}
            {creditsPeriodLabel}
          </Text>
          {billingNote && (
            <Text token="16/medium" className="text-tertiary">
              {billingNote}
            </Text>
          )}
        </div>
        <Button
          className={`w-full ${styles.buttonClassName}`}
          variant="primary"
          disabled={cta === 'Your current plan'}
          onClick={async () => {
            if (!product?.id) {
              return;
            }

            await handlePricingPlanSelection({
              authStatus,
              hasExistingSubscription,
              currentProductId,
              productId: product.id,
              stripeCustomerId,
              price,
              navigate,
            });
          }}
        >
          {cta}
        </Button>
      </div>
    </div>
  );
}

function formatDisplayMoney(unitAmount: number, interval: PriceInterval) {
  const displayAmountInDollars = updatedProductStore.formatMoney(
    interval === 'year' ? unitAmount / 12 : unitAmount,
  );

  if (interval === 'year') {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 0,
    }).format(Math.ceil(displayAmountInDollars));
  }

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: Number.isInteger(displayAmountInDollars) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(displayAmountInDollars);
}

function formatAnnualMoney(unitAmount: number) {
  const annualAmountInDollars = updatedProductStore.formatMoney(unitAmount);

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: Number.isInteger(annualAmountInDollars) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(annualAmountInDollars);
}
